import { useState } from 'react';
import { Calculator, TrendingUp, TrendingDown, Info, Loader2 } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { PageHeader, InputDecimal } from '../../components/ui';
import { Panel, Badge, buttonClass, card } from '../../design-system';
import { formatCurrency } from '../../utils/format';

interface SimulacaoResult {
  sistema: string;
  valorVeiculo: number;
  valorEntrada: number;
  valorFinanciado: number;
  taxaJurosMensal: number;
  taxaJurosAnual: number;
  quantidadeParcelas: number;
  valorParcela: number;
  valorUltimaParcela?: number;
  totalJuros: number;
  totalPago: number;
  parcelas: Parcela[];
}

interface Parcela {
  numero: number;
  valorParcela: number;
  juros: number;
  amortizacao: number;
  saldoDevedor: number;
  dataVencimento: string;
}

interface Comparacao {
  price: SimulacaoResult;
  sac: SimulacaoResult;
  diferencaJuros: number;
  economiaComSac: number;
  recomendacao: string;
}

interface Cenario {
  parcelas: number;
  valorParcela: number;
  totalJuros: number;
  totalPago: number;
}

interface CenariosResult {
  valorVeiculo: number;
  valorEntrada: number;
  valorFinanciado: number;
  taxaJurosMensal: number;
  cenarios: Cenario[];
}

export function SimuladorFinanciamento() {
  const { showError } = useNotification();

  const [valorVeiculo, setValorVeiculo] = useState<number | null>(50000);
  const [valorEntrada, setValorEntrada] = useState<number | null>(10000);
  const [taxaJuros, setTaxaJuros] = useState<number | null>(1.99);
  const [parcelas, setParcelas] = useState(48);

  const [isLoading, setIsLoading] = useState(false);
  const [comparacao, setComparacao] = useState<Comparacao | null>(null);
  const [cenarios, setCenarios] = useState<CenariosResult | null>(null);
  const [showParcelas, setShowParcelas] = useState<'price' | 'sac' | null>(null);

  const handleSimular = async () => {
    if (!valorVeiculo || valorVeiculo <= 0) {
      showError('Valor do veículo é obrigatório');
      return;
    }

    setIsLoading(true);
    try {
      const [compRes, cenRes] = await Promise.all([
        api.post<Comparacao>('/financiamento/comparar', {
          valorVeiculo,
          valorEntrada: valorEntrada || 0,
          taxaJurosMensal: taxaJuros || 1.99,
          quantidadeParcelas: parcelas,
        }),
        api.post<CenariosResult>('/financiamento/simular/cenarios', {
          valorVeiculo,
          valorEntrada: valorEntrada || 0,
          taxaJurosMensal: taxaJuros || 1.99,
          parcelasOpcoes: [12, 24, 36, 48, 60, 72],
        }),
      ]);

      setComparacao(compRes.data);
      setCenarios(cenRes.data);
    } catch {
      showError('Erro ao simular financiamento');
    } finally {
      setIsLoading(false);
    }
  };

  const valorFinanciado = (valorVeiculo || 0) - (valorEntrada || 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Simulador de Financiamento"
        subtitle="Compare tabela PRICE vs SAC"
      />

      {/* Formulário */}
      <div className={`${card} p-6`}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <InputDecimal
            label="Valor do Veículo *"
            prefix="R$ "
            value={valorVeiculo}
            onChange={setValorVeiculo}
          />

          <InputDecimal
            label="Valor de Entrada"
            prefix="R$ "
            value={valorEntrada}
            onChange={setValorEntrada}
          />

          <InputDecimal
            label="Taxa de Juros (% a.m.)"
            suffix="%"
            decimals={2}
            value={taxaJuros}
            onChange={setTaxaJuros}
          />

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Parcelas
            </label>
            <select
              value={parcelas}
              onChange={(e) => setParcelas(parseInt(e.target.value))}
              className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              {[12, 24, 36, 48, 60, 72, 84, 96].map((n) => (
                <option key={n} value={n}>
                  {n}x
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-slate-500">
            Valor a Financiar:{' '}
            <span className="font-semibold tabular-nums text-slate-900">
              {formatCurrency(valorFinanciado > 0 ? valorFinanciado : 0)}
            </span>
          </div>
          <button
            onClick={handleSimular}
            disabled={isLoading}
            className={buttonClass('primary', 'md')}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Calculator className="w-4 h-4" />
            )}
            Simular
          </button>
        </div>
      </div>

      {/* Cenários de Parcelas */}
      {cenarios && (
        <Panel title="Cenários de Parcelas (PRICE)">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
            {cenarios.cenarios.map((cen) => (
              <button
                key={cen.parcelas}
                onClick={() => setParcelas(cen.parcelas)}
                className={`rounded-xl border-2 p-4 text-center transition-all ${
                  parcelas === cen.parcelas
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <p className="text-lg font-bold tabular-nums text-slate-900">{cen.parcelas}x</p>
                <p className="text-sm font-medium tabular-nums text-primary-600">{formatCurrency(cen.valorParcela)}</p>
                <p className="mt-1 text-xs tabular-nums text-slate-400">
                  Juros: {formatCurrency(cen.totalJuros)}
                </p>
              </button>
            ))}
          </div>
        </Panel>
      )}

      {/* Comparação PRICE vs SAC */}
      {comparacao && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* Tabela PRICE */}
            <Panel title="Tabela PRICE" action={<Badge tone="primary">Parcelas Fixas</Badge>}>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Valor Financiado</span>
                  <span className="font-medium tabular-nums">{formatCurrency(comparacao.price.valorFinanciado)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Taxa (a.m. / a.a.)</span>
                  <span className="font-medium tabular-nums">
                    {comparacao.price.taxaJurosMensal}% / {comparacao.price.taxaJurosAnual.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-100 pt-3 text-lg">
                  <span className="font-semibold">Parcela</span>
                  <span className="font-bold tabular-nums text-primary-600">
                    {formatCurrency(comparacao.price.valorParcela)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total em Juros</span>
                  <span className="font-medium tabular-nums text-rose-600">
                    {formatCurrency(comparacao.price.totalJuros)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-100 pt-3 text-sm">
                  <span className="text-slate-500">Total a Pagar</span>
                  <span className="font-bold tabular-nums">{formatCurrency(comparacao.price.totalPago)}</span>
                </div>
              </div>

              <button
                onClick={() => setShowParcelas(showParcelas === 'price' ? null : 'price')}
                className="mt-4 w-full text-sm font-semibold text-primary-600 hover:text-primary-700"
              >
                {showParcelas === 'price' ? 'Ocultar parcelas' : 'Ver todas as parcelas'}
              </button>
            </Panel>

            {/* Tabela SAC */}
            <Panel title="Tabela SAC" action={<Badge tone="success">Amortização Constante</Badge>}>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Valor Financiado</span>
                  <span className="font-medium tabular-nums">{formatCurrency(comparacao.sac.valorFinanciado)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Taxa (a.m. / a.a.)</span>
                  <span className="font-medium tabular-nums">
                    {comparacao.sac.taxaJurosMensal}% / {comparacao.sac.taxaJurosAnual.toFixed(2)}%
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-100 pt-3 text-lg">
                  <span className="font-semibold">Primeira Parcela</span>
                  <span className="font-bold tabular-nums text-emerald-600">
                    {formatCurrency(comparacao.sac.valorParcela)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Última Parcela</span>
                  <span className="font-medium tabular-nums">
                    {formatCurrency(comparacao.sac.valorUltimaParcela || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total em Juros</span>
                  <span className="font-medium tabular-nums text-rose-600">
                    {formatCurrency(comparacao.sac.totalJuros)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-100 pt-3 text-sm">
                  <span className="text-slate-500">Total a Pagar</span>
                  <span className="font-bold tabular-nums">{formatCurrency(comparacao.sac.totalPago)}</span>
                </div>
              </div>

              <button
                onClick={() => setShowParcelas(showParcelas === 'sac' ? null : 'sac')}
                className="mt-4 w-full text-sm font-semibold text-emerald-600 hover:text-emerald-700"
              >
                {showParcelas === 'sac' ? 'Ocultar parcelas' : 'Ver todas as parcelas'}
              </button>
            </Panel>
          </div>

          {/* Recomendação */}
          <div className="rounded-2xl border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-4">
              <Info className="mt-0.5 h-6 w-6 flex-shrink-0 text-primary-600" />
              <div>
                <h3 className="mb-2 font-bold tracking-tight text-slate-900">Comparação</h3>
                <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-5 w-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-slate-500">Economia com SAC</p>
                      <p className="font-semibold tabular-nums text-emerald-600">{formatCurrency(comparacao.economiaComSac)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-rose-600" />
                    <div>
                      <p className="text-sm text-slate-500">Diferença de Juros</p>
                      <p className="font-semibold tabular-nums text-rose-600">{formatCurrency(comparacao.diferencaJuros)}</p>
                    </div>
                  </div>
                </div>
                <p className="text-slate-700">{comparacao.recomendacao}</p>
              </div>
            </div>
          </div>

          {/* Tabela de Parcelas */}
          {showParcelas && (
            <Panel title={`Detalhamento - ${showParcelas === 'price' ? 'PRICE' : 'SAC'}`}>
              <div className="max-h-96 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      <th className="p-2 font-bold">#</th>
                      <th className="p-2 font-bold">Parcela</th>
                      <th className="p-2 font-bold">Juros</th>
                      <th className="p-2 font-bold">Amortização</th>
                      <th className="p-2 font-bold">Saldo Devedor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showParcelas === 'price' ? comparacao.price.parcelas : comparacao.sac.parcelas).map(
                      (p) => (
                        <tr key={p.numero} className="border-t border-slate-50">
                          <td className="p-2 font-semibold tabular-nums">{p.numero}</td>
                          <td className="p-2 tabular-nums">{formatCurrency(p.valorParcela)}</td>
                          <td className="p-2 tabular-nums text-rose-600">{formatCurrency(p.juros)}</td>
                          <td className="p-2 tabular-nums text-emerald-600">{formatCurrency(p.amortizacao)}</td>
                          <td className="p-2 tabular-nums">{formatCurrency(p.saldoDevedor)}</td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}
